import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { makeRemoteUpdateUser } from '../main/factories/usecases/remote-update-user';
import { UpdateUserPayload } from '@hone-automation/common';
import { FIVE_SECONDS, showToast, TEN_SECONDS, TOAST_ERROR, TOAST_SUCCESS } from 'lib/utils';

/**
 * Custom hook for updating a user
 * @param {UseMutationOptions} options - Optional configuration options
 * @returns {Object} Mutation object with various properties and methods
 */
export const useUpdateUser = (
  options?: UseMutationOptions<
    any,
    Error,
    UpdateUserPayload & {
      locationId?: string;
      uid?: string;
    },
    unknown
  >
) => {
  const queryClient = useQueryClient();
  const updateUserService = makeRemoteUpdateUser();
  // Function to update the user via API
  const updateUserFn = async (userData: UpdateUserPayload) => {
    return updateUserService.update(userData);
  };

  const { onSuccess, ...rest } = options || {};

  return useMutation({
    mutationFn: updateUserFn,

    onError: (error, variables, _context) => {
      showToast(error.message, TOAST_ERROR, TEN_SECONDS);
    },

    // When the mutation succeeds, invalidate related queries
    onSuccess: async (updatedUser, variables, context) => {
      // Invalidate userInfo queries with correct key pattern
      await queryClient.invalidateQueries({
        queryKey: ['userInfo'],
        predicate: query => {
          const [key, locationId, userId] = query.queryKey;
          return (
            key === 'userInfo' &&
            locationId === variables.locationId &&
            (userId === variables.uid || userId === variables.userId)
          );
        },
      });

      // Force refetch of userInfo queries
      await queryClient.refetchQueries({
        queryKey: ['userInfo'],
        predicate: query => {
          const [key, locationId, userId] = query.queryKey;
          return (
            key === 'userInfo' &&
            locationId === variables.locationId &&
            (userId === variables.uid || userId === variables.userId)
          );
        },
      });

      // Invalidate the specific user query
      await queryClient.invalidateQueries({ queryKey: ['user', variables.userId] });

      // Optionally invalidate the users list query
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      showToast('User information updated successfully', TOAST_SUCCESS, FIVE_SECONDS);
      onSuccess?.(updatedUser, variables, context);
    },

    // Forward other options
    ...rest,
  });
};
